// FILE: assets/preorder.js
// 予約販売（Pre-order）機能 - 動的UI切り替え用JavaScript
//
// 予約モード判定条件:
// - 「在庫切れでも販売を続ける」がON (inventory_policy == 'continue')
// - かつ在庫数が0以下 (inventory_quantity <= 0)
// ※ メタフィールド不要。Liquid側で判定済みのvariant IDリストをdata属性で受け取る

/**
 * 予約販売機能を管理するクラス
 * - バリエーション切り替え時にボタンラベル・注意文・hidden inputs を動的に更新
 * - カートページで予約商品がある場合にカートノートを自動設定
 */
class PreorderManager {
  constructor() {
    this.init();
  }

  init() {
    // 商品ページ: バリエーション変更イベントをリッスン
    this.setupProductPageListeners();

    // カートページ: 予約商品があればカートノートを設定
    this.setupCartPageNote();
  }

  /**
   * 商品ページのバリエーション変更リスナーを設定
   */
  setupProductPageListeners() {
    // buy-buttons-wrapper 要素を取得
    const wrappers = document.querySelectorAll('.buy-buttons-wrapper');

    wrappers.forEach(wrapper => {
      const preorderVariantIdsStr = wrapper.dataset.preorderVariantIds || '';
      const preorderVariantIds = preorderVariantIdsStr
        ? preorderVariantIdsStr.split(',').map(id => id.trim())
        : [];

      if (preorderVariantIds.length === 0) {
        // 予約対象バリエーションがない場合は何もしない
        return;
      }

      // 設定値を取得
      const preorderButtonLabel = wrapper.dataset.preorderButtonLabel || '予約注文する';
      const preorderNoticeText = wrapper.dataset.preorderNoticeText || '';
      const preorderNote = wrapper.dataset.preorderNote || '';
      const normalButtonLabel = wrapper.dataset.normalButtonLabel || 'カートに追加';

      // フォーム内の要素を取得
      const form = wrapper.querySelector('form[data-type="add-to-cart-form"]');
      if (!form) return;

      const submitButton = form.querySelector('.product-form__submit');
      const buttonTextSpan = submitButton?.querySelector('.product-form__submit-text');
      const preorderFlagInput = form.querySelector('.preorder-flag-input');
      const preorderNoteInput = form.querySelector('.preorder-note-input');
      const preorderCustomerInput = form.querySelector('.preorder-customer-input');
      const preorderNotice = wrapper.querySelector('.preorder-notice');
      const preorderNoticeTextEl = preorderNotice?.querySelector('.preorder-notice__text');
      const variantIdInput = form.querySelector('input[name="id"]');

      if (!variantIdInput) return;

      /**
       * 予約モードかどうかをチェックして UI を更新
       */
      const updatePreorderState = (variantId) => {
        const isPreorder = preorderVariantIds.includes(String(variantId));

        // ボタンテキストを更新
        if (buttonTextSpan && !submitButton.disabled) {
          buttonTextSpan.textContent = isPreorder ? preorderButtonLabel : normalButtonLabel;
        }

        // ボタンのクラスを更新
        if (submitButton) {
          submitButton.classList.toggle('product-form__submit--preorder', isPreorder);
        }

        // 予約注意文の表示/非表示
        if (preorderNotice) {
          preorderNotice.classList.toggle('hidden', !isPreorder);
        }

        // hidden inputs を更新
        if (preorderFlagInput) {
          preorderFlagInput.value = isPreorder ? 'true' : 'false';
        }
        if (preorderNoteInput) {
          preorderNoteInput.value = isPreorder ? preorderNote : '';
        }
        if (preorderCustomerInput) {
          preorderCustomerInput.value = isPreorder ? (preorderCustomerInput.dataset.preorderValue || '') : '';
        }
      };

      // Dawn テーマの variant 変更イベント（variant:change）をリッスン
      // Dawn では product-info コンポーネントが variantChange イベントを発火
      document.addEventListener('variant:change', (event) => {
        const variantId = event.detail?.variant?.id;
        if (variantId) {
          updatePreorderState(variantId);
        }
      });

      // PubSub パターンによるバリエーション変更をリッスン
      // Dawn 15.x では subscribe を使用
      if (typeof subscribe === 'function') {
        subscribe('variantChange', (event) => {
          const variantId = event?.data?.variant?.id;
          if (variantId) {
            updatePreorderState(variantId);
          }
        });
      }

      // variant input の変更を監視（フォールバック）
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
            const newVariantId = variantIdInput.value;
            if (newVariantId) {
              updatePreorderState(newVariantId);
            }
          }
        });
      });

      observer.observe(variantIdInput, {
        attributes: true,
        attributeFilter: ['value']
      });

      // カスタムイベントとしても発火を検知
      variantIdInput.addEventListener('change', () => {
        updatePreorderState(variantIdInput.value);
      });
    });
  }

  /**
   * カートページで予約商品がある場合にカートノートを自動設定
   */
  setupCartPageNote() {
    // カートページかどうかを確認
    const cartForm = document.querySelector('form[action*="/cart"]');
    if (!cartForm) return;

    // カートノートのテキストエリアを取得
    const noteTextarea = document.querySelector('textarea[name="note"]');
    if (!noteTextarea) return;

    // カート内の予約商品をチェック
    // Liquid でレンダリングされた予約フラグを確認
    const preorderItems = document.querySelectorAll('[data-is-preorder="true"]');

    // もしくは、カートJSONから確認
    this.checkCartForPreorderItems(noteTextarea);
  }

  /**
   * カートAPIを使って予約商品をチェックし、必要ならノートを設定
   */
  async checkCartForPreorderItems(noteTextarea) {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      let hasPreorder = false;
      let preorderNoteText = '';

      cart.items.forEach(item => {
        // 2026-10-04 から印の名前は _is_preorder / _preorder_note（お客さまに見せない）。前の名前も読む
        const props = item.properties || {};
        if (props._is_preorder === 'true' || props.is_preorder === 'true') {
          hasPreorder = true;
          const note = props._preorder_note || props.preorder_note;
          if (note) {
            preorderNoteText = note;
          }
        }
      });

      if (hasPreorder && preorderNoteText) {
        // 既存のノートが空、または予約メモが含まれていない場合のみ追加
        const currentNote = noteTextarea.value.trim();
        if (!currentNote) {
          noteTextarea.value = preorderNoteText;
        } else if (!currentNote.includes(preorderNoteText)) {
          // 既存のノートの末尾に追記
          noteTextarea.value = currentNote + '\n\n' + preorderNoteText;
        }
      }
    } catch (error) {
      console.warn('[PreorderManager] カート情報の取得に失敗しました:', error);
    }
  }
}

// DOMContentLoaded 後に初期化
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new PreorderManager());
} else {
  new PreorderManager();
}
